import React, { useEffect, useRef, useState } from 'react';
import { ChevronRightIcon } from 'lucide-react';
import { ParallaxHero } from './ParallaxHero';
import { CtaLink } from './CtaLink';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import { heroStats } from '../data/heroStats';
const HEADLINE = 'Staviame PWA aplikácie poháňané multimodálnou AI.';
export function HeroSection() {
  const revealRef = useScrollReveal();
  const reducedMotion = usePrefersReducedMotion();
  const statsRef = useRef<HTMLDivElement>(null);
  const [typedText, setTypedText] = useState(reducedMotion ? HEADLINE : '');
  const [statsVisible, setStatsVisible] = useState(false);
  const [statValues, setStatValues] = useState(heroStats.map(() => 0));

  // Typing effect (skipped for reduced motion)
  useEffect(() => {
    if (reducedMotion) {
      setTypedText(HEADLINE);
      return;
    }
    let i = 0;
    const timer = setInterval(() => {
      i++;
      setTypedText(HEADLINE.slice(0, i));
      if (i >= HEADLINE.length) clearInterval(timer);
    }, 40);
    return () => clearInterval(timer);
  }, [reducedMotion]);

  // Start counters when stats scroll into view
  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setStatsVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.5
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!statsVisible) return;
    if (reducedMotion) {
      setStatValues(heroStats.map(s => s.value));
      return;
    }
    const steps = 50;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const eased = 1 - Math.pow(1 - step / steps, 2);
      setStatValues(heroStats.map(s => Math.round(eased * s.value)));
      if (step >= steps) clearInterval(timer);
    }, 36);
    return () => clearInterval(timer);
  }, [statsVisible, reducedMotion]);
  return <section id="prehlad" ref={revealRef} className="pt-28 md:pt-36 pb-20 md:pb-28 px-4 sm:px-6 lg:px-8 text-center max-w-7xl mx-auto" data-id="element-146" data-mp="3dsefrd044ewh">
      <div className="max-w-4xl mx-auto relative z-10" data-id="element-147" data-mp="3ipxt8aozknnd">
        <p className="reveal text-xs font-semibold tracking-[0.2em] text-apple-gray uppercase mb-6" data-id="element-148" data-mp="3mtm00r5snf7k">
          Nexify Studio × Google Gemini
        </p>

        <h1 aria-label={HEADLINE} className="reveal reveal-delay-1 text-4xl sm:text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.08] text-apple-black mb-6 min-h-[2.2em] md:min-h-[2.2em]" data-id="element-149" data-mp="3rrxcl9nh43th">
          <span aria-hidden="true" data-id="element-150" data-mp="abpfu9v1c3fs">
            {typedText}
            {typedText.length < HEADLINE.length && <span className="animate-pulse text-apple-blue" data-id="element-151" data-mp="edsuf7d03o5a">|</span>}
          </span>
        </h1>

        <p className="reveal reveal-delay-2 text-lg md:text-2xl font-light text-apple-gray mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-152" data-mp="jbm3c85ouw99">
          Premieňame Gemini AI na aplikácie, ktoré vidia, počujú a rozumejú vášmu biznisu.
        </p>

        <div className="reveal reveal-delay-3 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 mb-16 md:mb-20" data-id="element-153" data-mp="1yu0fdsn6wer6">
          <CtaLink href="#kontakt" className="w-full sm:w-auto btn-hover-premium" data-id="element-154" data-mp="1pun6b12z9s6z">
            Začať projekt
          </CtaLink>
          <a href="#portfolio" className="group inline-flex items-center min-h-[44px] px-2 text-base font-semibold text-apple-blue hover:text-apple-blueHover transition-colors" data-id="element-155" data-mp="2ml5wsc8s3f3l">
            Pozrieť portfólio
            <ChevronRightIcon className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform duration-200" aria-hidden="true" data-id="element-156" data-mp="2dqhn89w67b8z" />
          </a>
        </div>
      </div>

      <div className="reveal-scale reveal-delay-4" data-id="element-157" data-mp="369olkspvgqhr">
        <ParallaxHero data-id="element-158" data-mp="31igew4fxfj8a" />
      </div>

      <div ref={statsRef} className="reveal grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6 max-w-5xl mx-auto border-t border-gray-200 pt-12 md:pt-16" data-id="element-159" data-mp="31heq6x5zmy83">
        {heroStats.map((stat, i) => <div key={stat.label} className="flex flex-col items-center" data-id="element-160" data-mp="2q3bna2787lgv" data-mpi={i}>
            <span className="text-4xl md:text-6xl font-extrabold text-apple-black mb-2 tracking-tight tabular-nums" data-id="element-161" data-mp="2c30z56xawkze">
              {statValues[i]}
              {stat.suffix}
            </span>
            <span className="text-xs font-semibold text-apple-gray uppercase tracking-[0.15em]" data-id="element-162" data-mp="34qenigmxo87w">
              {stat.label}
            </span>
          </div>)}
      </div>
    </section>;
}